import {
  useCallback,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useSnackbar,
  useTable,
  useQueryTableFetch,
  useTranslations,
  useTableLocalization,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
} from '@uniqkey-frontend/shared-app';
import { useSearchParams } from 'react-router-dom';
import { GroupOrganizationOrderProperty } from '@uniqkey-backend-partner/api-client';
import { useQueryClient } from 'react-query';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import ReactQueryKeyEnum from '../../../enums/ReactQueryKeyEnum';
import {
  IGetGroupOrganizationsForAdminParams,
  TGetGroupOrganizationsForAdminMethod,
} from '../../useGroupsAPI/interfaces';
import { IGroupOrganizationsForAdminTableRow } from
  '../../../components/tables/GroupOrganizationsForAdminTable';
import useGroupsAPI from '../../useGroupsAPI';

export const REACT_QUERY_GROUP_ORGANIZATIONS_FOR_ADMIN_KEY = [
  ReactQueryKeyEnum.GroupOrganizationsForAdmin,
];

interface IPersistentFilters extends Pick<IGetGroupOrganizationsForAdminParams, 'groupId'> {}

export interface IUseGroupOrganizationsForAdminTableParams {
  persistentFilters?: IPersistentFilters;
  noDataMessageKey: string;
}

export interface IUseGroupOrganizationsForAdminTableReturn extends
  Omit<IUseTableReturn<IGroupOrganizationsForAdminTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage' | 'resetSelectedRows'>,
  Omit<
    IUseQueryTableFetchReturn<TGetGroupOrganizationsForAdminMethod>,
    'data' | 'isFetchedAfterMount'
  >
{
  groupOrganizationsForAdmin: IUseQueryTableFetchReturn<
    TGetGroupOrganizationsForAdminMethod
  >['data'];
  selectedGroupOrganizationsForAdmin: IUseTableReturn<
    IGroupOrganizationsForAdminTableRow
  >['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
}

interface ITablePreservationConfigs extends
  Omit<IGetGroupOrganizationsForAdminParams,
    'page' | 'pageLength' | 'groupId' | 'orderPropertyName' | 'isDescending'
  >,
  Pick<IUseTableReturn<IGroupOrganizationsForAdminTableRow>,
    'columnOrderBy' | 'columnOrderDirection'
  >
{
  activePage: IGetGroupOrganizationsForAdminParams['page'];
  perPage: IGetGroupOrganizationsForAdminParams['pageLength'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: GroupOrganizationOrderProperty },
  ),
  columnOrderDirection: true,
};

const useGroupOrganizationsForAdminTable = (
  params: IUseGroupOrganizationsForAdminTableParams,
): IUseGroupOrganizationsForAdminTableReturn => {
  const {
    persistentFilters,
    noDataMessageKey,
  } = params;

  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const { showError } = useSnackbar();
  const { getGroupOrganizationsForAdmin } = useGroupsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedGroupOrganizationsForAdmin,
    resetTableMethods,
    ...restTableProps
  } = useTable<IGroupOrganizationsForAdminTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
    resetSelectedRows,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => resetTable(), [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: groupOrganizationsForAdmin, isLoading, isFetchedAfterMount, total, resetQuery,
  } = useQueryTableFetch({
    queryKey: REACT_QUERY_GROUP_ORGANIZATIONS_FOR_ADMIN_KEY,
    queryClient,
    request: getGroupOrganizationsForAdmin,
    params: {
      page: activePage,
      pageLength: perPage,
      orderPropertyName: GroupOrganizationOrderProperty[
        upperFirst(columnOrderBy) as keyof typeof GroupOrganizationOrderProperty
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
    autoRefetch: true,
  });

  const { localization } = useTableLocalization({
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
  });

  return {
    groupOrganizationsForAdmin,
    isLoading,
    total,
    resetQuery,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedGroupOrganizationsForAdmin,
    localization,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps,
  };
};

export default useGroupOrganizationsForAdminTable;
